import { brand } from '../../lib/brand';
import { createContext, useContext, useState, type ReactNode } from 'react';
import { IconMoon, IconSun } from '@tabler/icons-react';
import { readAdminTheme, saveAdminTheme, type AdminTheme } from '../../lib/adminTheme';
import { useLocation } from 'react-router-dom';
import { deploymentIdentity } from '../../lib/deployment';

const identity = deploymentIdentity(__DEPLOY_ENV__, brand.name);

const AppearanceContext = createContext<{ theme: AdminTheme; toggle: () => void }>({
  theme: 'light',
  toggle: () => {},
});

export function AppearanceToggle() {
  const { theme, toggle } = useContext(AppearanceContext);
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label="深色模式"
      aria-pressed={theme === 'dark'}
      className="inline-flex items-center justify-center gap-2 border border-base-300 px-3 py-2 text-sm text-base-content hover:bg-base-300"
    >
      {theme === 'dark' ? (
        <IconSun size={18} stroke={1.5} aria-hidden="true" />
      ) : (
        <IconMoon size={18} stroke={1.5} aria-hidden="true" />
      )}
      <span>{theme === 'dark' ? '淺色' : '深色'}</span>
    </button>
  );
}

export function DesignBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const [theme, setTheme] = useState<AdminTheme>(() => {
    try {
      return readAdminTheme(window.localStorage);
    } catch {
      return 'light';
    }
  });
  const toggle = () => {
    const next = theme === 'light' ? 'dark' : 'light';
    setTheme(next);
    try {
      saveAdminTheme(window.localStorage, next);
    } catch {
      /* Storage access itself may be blocked. */
    }
  };
  const isScreen = /^\/(tv(?:\/|$)|display\/screen\/)/.test(pathname);
  return (
    <AppearanceContext.Provider value={{ theme, toggle }}>
      <div
        className="zipa-application"
        data-context={isScreen ? 'venue-screen' : 'admin'}
        data-admin-theme={theme}
        data-design-version="1.0.0"
        data-deployment={identity.environment}
      >
        {identity.isTest && (
          <div className="zipa-environment" role="status">
            {identity.label}
          </div>
        )}
        <div className="zipa-viewport">{children}</div>
      </div>
    </AppearanceContext.Provider>
  );
}
